import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { Button } from "../components/button";
import { Icon } from "../components/icon";
import { cn } from "../lib/cn";

export const MAIN_CONTENT_ID = "main";
const SIDEBAR_ID = "app-sidebar";

interface ShellContextValue {
  sidebarOpen: boolean;
  openSidebar: () => void;
  closeSidebar: () => void;
  sidebarId: string;
}

const ShellContext = createContext<ShellContextValue | null>(null);

export function useShell(): ShellContextValue {
  const context = useContext(ShellContext);
  if (!context) throw new Error("useShell must be used inside <AppShell>");
  return context;
}

export interface AppShellLabels {
  skipToContent: string;
  closeMenu: string;
  sidebar: string;
  mainNav: string;
}

export interface AppShellProps {
  brand: ReactNode;
  sidebarTop?: ReactNode;
  nav: ReactNode;
  sidebarFooter?: ReactNode;
  topbar: ReactNode;
  children: ReactNode;
  /** `<main data-flush>` in the mockups: no padding, full-height panes. */
  flush?: boolean;
  labels: AppShellLabels;
  /** Changes when the route changes; closes the mobile sidebar. */
  locationKey?: string;
}

export function SkipLink({ label }: { label: string }) {
  return (
    <a
      href={`#${MAIN_CONTENT_ID}`}
      className="btn btn-primary sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-3 focus:z-[70]"
    >
      {label}
    </a>
  );
}

/** Navy sidebar + sticky topbar layout from mockups/assets/app.js `buildShell()`. */
export function AppShell({ brand, sidebarTop, nav, sidebarFooter, topbar, children, flush = false, labels, locationKey }: AppShellProps) {
  const [openForKey, setOpenForKey] = useState<string | null>(null);
  const currentKey = locationKey ?? "";
  const sidebarOpen = openForKey === currentKey;
  const openSidebar = useCallback(() => {
    setOpenForKey(currentKey);
  }, [currentKey]);
  const closeSidebar = useCallback(() => {
    setOpenForKey(null);
  }, []);

  useEffect(() => {
    if (!sidebarOpen) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeSidebar();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, [sidebarOpen, closeSidebar]);

  const value = useMemo(
    () => ({ sidebarOpen, openSidebar, closeSidebar, sidebarId: SIDEBAR_ID }),
    [sidebarOpen, openSidebar, closeSidebar],
  );

  return (
    <ShellContext.Provider value={value}>
      <div className="min-h-screen">
        <SkipLink label={labels.skipToContent} />
        {sidebarOpen ? (
          <div aria-hidden="true" className="fixed inset-0 z-30 bg-ink-950/50 lg:hidden" onClick={closeSidebar} />
        ) : null}
        <aside
          id={SIDEBAR_ID}
          aria-label={labels.sidebar}
          className={cn(
            "fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-chrome-line bg-chrome transition-transform duration-200 motion-reduce:transition-none lg:visible lg:translate-x-0",
            sidebarOpen ? "translate-x-0" : "invisible -translate-x-full",
          )}
        >
          <div className="flex h-14 shrink-0 items-center justify-between border-b border-chrome-line px-4">
            {brand}
            <Button variant="ghost" size="sm" iconOnly aria-label={labels.closeMenu} className="text-chrome-muted lg:hidden" onClick={closeSidebar}>
              <Icon name="x" />
            </Button>
          </div>
          {sidebarTop ? <div className="relative">{sidebarTop}</div> : null}
          <nav aria-label={labels.mainNav} className="flex-1 overflow-y-auto px-3 pb-4 pt-1">
            {nav}
          </nav>
          {sidebarFooter ? (
            <div className="mt-auto space-y-2 border-t border-chrome-line px-3 pb-3 pt-3">{sidebarFooter}</div>
          ) : null}
        </aside>
        <div className="flex min-h-screen min-w-0 flex-col lg:pl-64">
          {topbar}
          <main
            id={MAIN_CONTENT_ID}
            tabIndex={-1}
            className={cn("outline-none", flush ? "min-h-0 flex-1" : "flex-1 px-4 py-6 lg:px-8 lg:py-8")}
          >
            {children}
          </main>
        </div>
      </div>
    </ShellContext.Provider>
  );
}
